/* eslint-disable @typescript-eslint/no-explicit-any */
// import AmexCard from 'assets/common/cardLogos/amex.svg';
// import CreditCard from 'assets/common/cardLogos/credit-card-regular.svg';
// import DiscoverCard from 'assets/common/cardLogos/discover.png';
// import MasterCard from 'assets/common/cardLogos/mastercard.svg';
// import VisaCard from 'assets/common/cardLogos/visa.svg';
import { getFirebaseAuth } from 'configs/firebaseConfig';
import dayjs from 'dayjs';
import { getPartnerName } from 'partners';
import { TPlanList, TSupportedFeature } from 'typings';
import { partnerAssets } from '../partners/partnersAssets';
import { HUB_PLAN_FEATURE_ID } from './subscriptionFunctions';

export const firebaseLogout = async () => {
  const { auth } = await getFirebaseAuth();
  const { signOut } = await import('firebase/auth');
  await signOut(auth);
};

export const setLocalStorageItem = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

export const getLocalStorageItem = (key: string) => {
  return localStorage.getItem(key);
};

export const removeLocalStorageItem = (key: string) => {
  localStorage.removeItem(key);
};

type PlanData = {
  sku_id: string;
  level: number;
  name: string;
};

const planData: PlanData[] = [
  { sku_id: 'ZJkWuV4hp9eUn7XOWpsgEpSmVfz', level: 1, name: 'Basic plan' },
  { sku_id: 'ZJkWuXu0cnmul6yD1mjsYZqdY5Z', level: 1, name: 'Basic plan' },
  { sku_id: 'ZJkWubZWLzeLEtN4sOJcg37RWCY', level: 2, name: 'Multi-Standard' },
  { sku_id: 'ZJkWuVTRkISYUmL0izTsM5N19pt', level: 2, name: 'Multi-Standard' },
  {
    sku_id: 'ZJkWuUCZH1GGokomjgTV8c4KtsI',
    level: 3,
    name: 'Premium Security'
  },
  {
    sku_id: 'ZJkWuZ02nDssfHl91aUO3AXxLxH',
    level: 3,
    name: 'Premium Security'
  },
  { sku_id: 'ZJkWuWg4Wa0jreEthneCxO1rZPn', level: 4, name: 'Combo' },
  { sku_id: 'ZJkWuXRsXMgDVk6AMbMTBliGijd', level: 4, name: 'Combo' }
];

export function getPlanNameByLevel(level: number): string {
  const plan = planData.find((item) => item.level === level);
  return plan ? plan.name : '';
}

export function nextUpgradePlan(sku_id: string | undefined): string {
  if (!sku_id) return '';
  const currentPlan = planData.find((plan) => plan.sku_id === sku_id);
  if (currentPlan) {
    const currentLevel = currentPlan.level;
    const nextLevel = currentLevel + 1;

    const nextPlan = planData.find((plan) => plan.level === nextLevel);
    if (nextPlan) {
      return nextPlan.name;
    }
  }
  return '';
}

export function isCardValid(cardMonth: number, cardYear: number) {
  const currentMonth = dayjs().month() + 1;
  const currentYear = dayjs().year();
  if (currentYear > cardYear) {
    return false;
  }
  if (currentYear === cardYear) {
    if (currentMonth > cardMonth) {
      return false;
    }
    return true;
  }
  return true;
}

export function calculateRemainingDays(
  trialStartTime: string,
  freeTrialDays: number
) {
  const startDate = dayjs(trialStartTime);
  const endDate = startDate.add(freeTrialDays, 'day');
  const currentDate = dayjs();
  const remainingDays = endDate.diff(currentDate, 'day');
  return remainingDays;
}

export function getPartnerId() {
  const partnerName = getPartnerName();
  return partnerName;
}

export const getCardImageProps = (cardBrand: string) => {
  const brand = cardBrand?.toLowerCase();
  switch (brand) {
    case 'visa':
      return { src: partnerAssets.visaCard, width: '52', height: '16' };
    case 'mastercard':
      return { src: partnerAssets.masterCard, width: '40', height: '30' };
    case 'amex':
      return { src: partnerAssets.amexCard, width: '40', height: '16' };
    case 'discover':
      return { src: partnerAssets.discoverCard, width: '40', height: '30' };
    default:
      return {
        src: partnerAssets.creditRegularCard,
        width: '40',
        height: '24'
      };
  }
};

/**
 * Decodes a Base64URL-encoded string.
 * @param base64Url - The Base64URL string to decode.
 * @returns The decoded JSON object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decodeBase64Url = (base64Url: string): any => {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('')
  );
  return JSON.parse(jsonPayload);
};

/**
 * Checks whether a JWT token's expiry time has passed.
 * Returns false for opaque (non-JWT) tokens or tokens without an exp claim.
 * @param token - The JWT token.
 * @returns boolean - True if the token is expired, otherwise false.
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const [, payloadBase64] = token.split('.');
    if (!payloadBase64) {
      throw new Error('Invalid token format');
    }

    const payload = decodeBase64Url(payloadBase64);

    if (!payload.exp) {
      throw new Error('Token does not have an expiry time');
    }

    return payload.exp <= Math.floor(Date.now() / 1000);
  } catch (error) {
    console.error('Error parsing token:', error);
    return false;
  }
};

type FractionDigits = {
  min: number;
  max: number;
};

export const getFractionDigitsForCountry = (
  country: string
): FractionDigits => {
  const normalizedCountry = country?.toUpperCase();

  // Example rule: no decimals for Spanish-speaking countries like MX
  switch (normalizedCountry) {
    case 'MX':
      return { min: 0, max: 0 };
    case 'BR':
      return { min: 0, max: 0 };
    default:
      return { min: 2, max: 2 }; // Default format with decimals
  }
};

export const formatPrice = (
  priceInCents: number = 0,
  currency: string = 'USD',
  country: string = 'US',
  currentLocale: string = 'en-US'
): string => {
  const { min, max } = getFractionDigitsForCountry(country);

  const factor = 10 ** max;
  const truncatedPrice = Math.floor(priceInCents / (100 / factor)) / factor;

  const formatter = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: min,
    maximumFractionDigits: max
  });

  return formatter.format(truncatedPrice);
};

export const getSupportedCameraCount = (
  supported_features: TSupportedFeature[]
) => {
  if (!supported_features || supported_features.length === 0) {
    return null;
  }
  const cameraFeature = supported_features?.find(
    (item: TSupportedFeature) => item.id === 'cameras_supported'
  );
  return Number(cameraFeature?.value) || null;
};

/**
 * Hubs a plan covers, from the SKU's `hubs_supported` feature. Null when the
 * plan doesn't list it, matching `getSupportedCameraCount`.
 */
export const getSupportedHubCount = (
  supported_features: TSupportedFeature[]
) => {
  const hubFeature = supported_features?.find(
    (item: TSupportedFeature) => item.id === HUB_PLAN_FEATURE_ID
  );
  return Number(hubFeature?.value) || null;
};

/**
 * MB granted during a free trial, as the SKU reports it — the
 * `free_data_in_mb` feature, whose `value` is a bare number of megabytes.
 * Null when the plan carries no such feature, so callers can fall back
 * instead of rendering a blank figure.
 */
export const getFreeTrialDataMb = (
  supported_features: TSupportedFeature[]
): number | null => {
  const feature = supported_features?.find(
    (item: TSupportedFeature) => item.id === 'free_data_in_mb'
  );
  return Number(feature?.value) || null;
};

/**
 * The largest free-trial grant across a set of plans. Combo tiers differ
 * (Solo vs Enhanced / Premium), and the heading quotes the range as "up to".
 */
export const getMaxFreeTrialDataMb = (plans: TPlanList[]): number | null => {
  const values = plans
    .map((plan) => getFreeTrialDataMb(plan.supported_features))
    .filter((value): value is number => value !== null);
  return values.length ? Math.max(...values) : null;
};

export const getSupported4GData = (supported_features: TSupportedFeature[]) => {
  if (!supported_features || supported_features.length === 0) {
    return 1;
  }
  const cameraFeature = supported_features?.find(
    (item: TSupportedFeature) => item.id === '4g_data'
  );
  return cameraFeature?.value || 'NA';
};

export const downloadFile = ({
  url,
  filename
}: {
  url: string;
  filename: string;
}) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'invoice.pdf'; // Default filename
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const truncateObject = (obj: any, maxLength: number): string => {
  if (typeof obj !== 'object' || obj === null) {
    return String(obj).substring(0, maxLength);
  }

  try {
    const str = JSON.stringify(obj);
    return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
  } catch (err) {
    return '[circular or non-serializable object]';
  }
};

export const calculatePercentageOff = (
  originalPrice: number,
  currentPrice: number
): number => {
  if (originalPrice <= 0) return 0; // Prevent division by zero or invalid input
  return ((originalPrice - currentPrice) / originalPrice) * 100;
};

export const getMaxDiscountPercentage = (plans: TPlanList[]): number => {
  const maxDiscount = Math.max(
    0,
    ...plans.flatMap((plan) =>
      plan.prices
        .filter(
          (price) => price.discount_coupon?.amount && price.original_price > 0
        )
        .map(
          (price) =>
            (price.discount_coupon!.amount / price.original_price) * 100
        )
    )
  );

  return Math.ceil(maxDiscount / 10) * 10;
};

export const toISODate = (epochMs: number | undefined) =>
  epochMs ? new Date(epochMs).toISOString() : undefined;

/**
 * Uppercases the first character, preserving literal types: passing a string
 * union returns the Capitalize<> of that union ('card' -> 'Card').
 */
export const capitalize = <T extends string>(value: T): Capitalize<T> =>
  (value.charAt(0).toUpperCase() + value.slice(1)) as Capitalize<T>;
